import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Input, Select, message, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { api } from '../services/api';

interface Barcode {
  id?: number;
  productId?: number;
  productVariantId?: number;
  barcode: string;
  barcodeType: string;
  isPrimary: boolean;
}

interface ProductBarcodeManagerProps {
  productId?: number;
  variantId?: number; // Varyant düzenleme için
  onChange?: (barcodes: Barcode[]) => void;
  showBulkGenerate?: boolean; // Toplu üret butonunu göster
}

const BARCODE_TYPES = ['EAN-13', 'EAN-8', 'UPC-A', 'UPC-E', 'Code128', 'Code39', 'QR'];

const ProductBarcodeManager: React.FC<ProductBarcodeManagerProps> = ({ productId, variantId, onChange, showBulkGenerate = false }) => {
  const [barcodes, setBarcodes] = useState<Barcode[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState<number | string>('');
  const [newBarcode, setNewBarcode] = useState('');
  const [newBarcodeType, setNewBarcodeType] = useState('EAN-13');

  useEffect(() => {
    if (variantId) {
      loadVariantBarcodes(variantId);
    } else if (productId) {
      loadBarcodes(productId);
    } else {
      setBarcodes([]);
    }
  }, [productId, variantId]);

  const loadBarcodes = async (pId: number) => {
    try {
      setLoading(true);
      const { data } = await api.get<Barcode[]>(`/productbarcodes/product/${pId}`);
      setBarcodes(data);
      if (onChange) onChange(data);
    } catch (error) {
      message.error('Barkodlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadVariantBarcodes = async (vId: number) => {
    try {
      setLoading(true);
      const { data } = await api.get<Barcode[]>(`/productbarcodes/variant/${vId}`);
      setBarcodes(data);
      if (onChange) onChange(data);
    } catch (error) {
      message.error('Varyant barkodları yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // EAN-13 uyumlu internal barkod üretir
  const generateInternalBarcode = (productIdValue: number, companyPrefix: string = '999'): string => {
    const base = `2${companyPrefix}${productIdValue.toString().padStart(9, '0')}`; // 12 haneli gövde
    const digits = base.split('').map(d => parseInt(d));
    
    // Check digit hesaplama (EAN-13)
    let total = 0;
    for (let i = 0; i < digits.length; i++) {
      if (i % 2 === 0) {
        total += digits[i];
      } else {
        total += digits[i] * 3;
      }
    }
    const checkDigit = (10 - (total % 10)) % 10;
    return base + checkDigit.toString();
  };

  // Benzersiz barkod üretir (veritabanında mevcut olanları kontrol eder)
  const generateUniqueInternalBarcode = async (productIdValue: number, companyPrefix: string = '999'): Promise<string> => {
    let attempt = 0;
    let candidateBarcode = '';
    
    while (attempt < 100) { // Sonsuz döngüyü önlemek için limit
      const candidateId = productIdValue + attempt;
      candidateBarcode = generateInternalBarcode(candidateId, companyPrefix);
      
      // Mevcut barkodlarda var mı kontrol et
      const existsLocally = barcodes.some(b => b.barcode === candidateBarcode);
      if (!existsLocally) {
        // API'den de kontrol et
        try {
          const { data } = await api.get(`/productbarcodes/check/${candidateBarcode}`);
          if (!data.exists) {
            return candidateBarcode;
          }
        } catch (error) {
          // Eğer endpoint yoksa, sadece lokal kontrol yeterli
          return candidateBarcode;
        }
      }
      attempt++;
    }
    
    throw new Error('Benzersiz barkod üretilemedi');
  };

  const handleAutoGenerate = async () => {
    const idToUse = variantId || productId;
    if (!idToUse) {
      message.warning('Otomatik barkod üretmek için önce ürünü/varyantı kaydetmelisiniz');
      return;
    }

    try {
      setLoading(true);
      const generatedBarcode = await generateUniqueInternalBarcode(idToUse);
      setNewBarcode(generatedBarcode);
      setNewBarcodeType('EAN-13');
      message.success('Barkod otomatik üretildi');
    } catch (error: any) {
      message.error(error.message || 'Barkod üretilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGenerate = async () => {
    if (!productId) {
      message.warning('Toplu barkod üretmek için önce ürünü kaydetmelisiniz');
      return;
    }

    try {
      setLoading(true);
      const { data } = await api.post(`/productbarcodes/bulk-generate/${productId}`);
      message.success(`${data.length} adet varyant için barkod üretildi`);
      loadBarcodes(productId);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Toplu barkod üretilirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!newBarcode.trim()) {
      message.warning('Barkod giriniz');
      return;
    }

    // Aynı barkod var mı kontrol et
    if (barcodes.some(b => b.barcode === newBarcode.trim())) {
      message.warning('Bu barkod zaten mevcut');
      return;
    }

    const newBarcodeObj: Barcode = {
      barcode: newBarcode.trim(),
      barcodeType: newBarcodeType,
      isPrimary: barcodes.length === 0, // İlk barkod birincil olsun
    };

    if (productId || variantId) {
      // API'ye kaydet
      saveBarcodeToApi(newBarcodeObj);
    } else {
      // Henüz ürün kaydedilmemiş, lokal state'e ekle
      const updatedBarcodes = [...barcodes, { ...newBarcodeObj, id: Date.now() }];
      setBarcodes(updatedBarcodes);
      if (onChange) onChange(updatedBarcodes);
      setNewBarcode('');
      message.success('Barkod eklendi (Ürün kaydedildiğinde veritabanına yazılacak)');
    }
  };

  const saveBarcodeToApi = async (barcode: Barcode) => {
    try {
      setLoading(true);
      const { data } = await api.post('/productbarcodes', {
        ...barcode,
        productId: productId || barcode.productId,
        productVariantId: variantId,
      });
      message.success('Barkod başarıyla eklendi');
      if (variantId) {
        loadVariantBarcodes(variantId);
      } else if (productId) {
        loadBarcodes(productId);
      }
      setNewBarcode('');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Barkod eklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record: Barcode) => {
    if (productId && record.id) {
      // API'den sil
      try {
        setLoading(true);
        await api.delete(`/productbarcodes/${record.id}`);
        message.success('Barkod başarıyla silindi');
        loadBarcodes(productId);
      } catch (error) {
        message.error('Barkod silinirken hata oluştu');
      } finally {
        setLoading(false);
      }
    } else {
      // Lokal state'den sil
      const updatedBarcodes = barcodes.filter(b => b.id !== record.id);
      setBarcodes(updatedBarcodes);
      if (onChange) onChange(updatedBarcodes);
      message.success('Barkod silindi');
    }
  };

  const handleSetPrimary = async (record: Barcode) => {
    if (productId && record.id) {
      try {
        setLoading(true);
        await api.put(`/productbarcodes/${record.id}/setprimary`);
        message.success('Birincil barkod güncellendi');
        loadBarcodes(productId);
      } catch (error) {
        message.error('Birincil barkod ayarlanırken hata oluştu');
      } finally {
        setLoading(false);
      }
    } else {
      // Lokal state'de güncelle
      const updatedBarcodes = barcodes.map(b => ({
        ...b,
        isPrimary: b.id === record.id,
      }));
      setBarcodes(updatedBarcodes);
      if (onChange) onChange(updatedBarcodes);
      message.success('Birincil barkod ayarlandı');
    }
  };

  const columns = [
    {
      title: 'Barkod',
      dataIndex: 'barcode',
      key: 'barcode',
      width: '40%',
    },
    {
      title: 'Barkod Tipi',
      dataIndex: 'barcodeType',
      key: 'barcodeType',
      width: '20%',
    },
    {
      title: 'Durum',
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      width: '20%',
      render: (isPrimary: boolean, record: Barcode) => (
        <Space>
          {isPrimary ? (
            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>✓ Birincil</span>
          ) : (
            <Button type="link" size="small" onClick={() => handleSetPrimary(record)}>
              Birincil Yap
            </Button>
          )}
        </Space>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      width: '20%',
      render: (_: any, record: Barcode) => (
        <Popconfirm
          title="Bu barkodu silmek istediğinizden emin misiniz?"
          onConfirm={() => handleDelete(record)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button type="link" danger icon={<DeleteOutlined />} size="small">
            Sil
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card title="Barkodlar" size="small">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Toplu Barkod Üretme - Sadece ürün düzenlemede göster */}
        {showBulkGenerate && productId && (
          <Button 
            type="dashed" 
            icon={<ThunderboltOutlined />} 
            onClick={handleBulkGenerate} 
            loading={loading}
            block
            style={{ borderColor: '#52c41a', color: '#52c41a' }}
          >
            ⚡ Tüm Varyantlara Otomatik Barkod Ata
          </Button>
        )}

        {/* Yeni Barkod Ekleme */}
        <Space style={{ width: '100%' }} wrap>
          <Input
            placeholder="Barkod numarası giriniz"
            value={newBarcode}
            onChange={(e) => setNewBarcode(e.target.value)}
            onPressEnter={handleAdd}
            style={{ width: '240px' }}
          />
          <Select
            placeholder="Barkod tipi"
            value={newBarcodeType}
            onChange={(value) => setNewBarcodeType(value)}
            style={{ width: '140px' }}
          >
            {BARCODE_TYPES.map(type => (
              <Select.Option key={type} value={type}>
                {type}
              </Select.Option>
            ))}
          </Select>
          <Button 
            type="default" 
            icon={<ThunderboltOutlined />} 
            onClick={handleAutoGenerate} 
            loading={loading}
            disabled={!productId && !variantId}
            title={(!productId && !variantId) ? 'Önce kaydedin' : 'Otomatik barkod üret'}
          >
            Otomatik Üret
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} loading={loading}>
            Ekle
          </Button>
        </Space>

        {/* Barkod Tablosu */}
        <Table
          columns={columns}
          dataSource={barcodes}
          rowKey={(record) => record.id?.toString() || record.barcode}
          loading={loading}
          pagination={false}
          size="small"
          locale={{
            emptyText: 'Henüz barkod eklenmemiş',
          }}
        />

        {!productId && barcodes.length > 0 && (
          <div style={{ color: '#faad14', fontSize: '12px' }}>
            ℹ️ Barkodlar ürün kaydedildiğinde veritabanına yazılacaktır.
          </div>
        )}
      </Space>
    </Card>
  );
};

export default ProductBarcodeManager;

